import { useReportDateState } from "../hooks/useReportDateAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../utils/localDate";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { API_ENDPOINTS } from "../config/constants";
import { getOfflineCategorySalesReport } from "../offline/financeStore";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import {
  buildCategorySalesGroups,
  buildCategorySalesReceiptText,
} from "../utils/categorySalesReport";
import {
  isDesktopApp,
  indiaDateKey,
  numberValue,
  extractOrderItems,
  getCachedOrderDetail,
  getLocalProductAndCategoryMaps,
  getCategoryNameFromItem,
  DESKTOP_CACHE_HEADER,
} from "../utils/offlineReportUtils";

const REPORT_MODE_DAY = "day";
const REPORT_MODE_MONTH = "month";
const DAY_ENDPOINT = API_ENDPOINTS.REPORT_DAY_WISE_ITEMS;
const MONTH_ENDPOINT = API_ENDPOINTS.REPORT_MONTH_WISE_ITEMS;

const buildDefaultReport = ({ mode, date, month }) => ({
  mode,
  date,
  month,
  restaurant: "",
  total_items: 0,
  total_quantity: 0,
  total_amount: 0,
  total_orders: 0,
  items: [],
});

const aggregateCategories = async (items = [], restaurantId = null) => {
  const { productMap, productByNameMap, categoryMap: localCatMap } = await getLocalProductAndCategoryMaps(restaurantId);
  return buildCategorySalesGroups(items, (item) =>
    getCategoryNameFromItem(item, productMap, productByNameMap, localCatMap)
  );
};

const buildOfflineCategoryReport = async (restaurantId, mode, selectedDate, selectedMonth) => {
  if (!isDesktopApp()) return null;
  const { productMap, productByNameMap, categoryMap: localCatMap } = await getLocalProductAndCategoryMaps(restaurantId);

  const [cachedOrders, pendingRows] = await Promise.all([
    window.desktopOrders.list({ restaurantId }),
    window.desktopOrderQueue?.list({ restaurantId, includeDeferred: true }) || [],
  ]);

  const matchesFilter = (createdAt) => {
    const dKey = indiaDateKey(createdAt);
    if (!dKey) return false;
    if (mode === REPORT_MODE_MONTH) {
      return dKey.slice(0, 7) === selectedMonth;
    }
    return dKey === selectedDate;
  };

  const orders = (cachedOrders || []).filter((order) =>
    matchesFilter(order?.created_at ?? order?.createdAt ?? order?.date)
  );

  const pendingOrders = (pendingRows || [])
    .filter((entry) => matchesFilter(entry?.createdAt))
    .map((entry) => ({
      id: `pending-${entry.id}`,
      created_at: entry.createdAt,
      itemlist: entry?.payload?.itemlist || [],
      payload: entry.payload,
      is_pending_sync: true,
    }));

  const allOrders = [...pendingOrders, ...orders];
  const salesItems = [];
  let ordersWithItems = 0;
  let missingDetailCount = 0;

  await Promise.all(allOrders.map(async (order) => {
    const detail = order?.is_pending_sync ? null : await getCachedOrderDetail(restaurantId, order);
    const items = extractOrderItems(detail, order);
    if (items.length) {
      ordersWithItems += 1;
    } else if (!order?.is_pending_sync) {
      missingDetailCount += 1;
    }

    const seenInOrder = new Set();
    items.forEach((item) => {
      const categoryName = getCategoryNameFromItem(item, productMap, productByNameMap, localCatMap);

      const productId = item?.product_id ?? item?.productid ?? item?.id ?? "";
      const itemName = String(
        item?.item_name ?? item?.product_name ?? item?.productname ?? item?.name ?? "Item"
      ).trim() || "Item";
      const itemKey = String(productId || `${categoryName}:${itemName}`).toLowerCase();

      const quantity = numberValue(item?.total_quantity ?? item?.qty ?? item?.quantity ?? item?.qtyy);
      const lineAmount = item?.total_amount !== undefined
        ? numberValue(item.total_amount)
        : item?.amount !== undefined
        ? numberValue(item.amount)
        : quantity * numberValue(item?.priceaftertax ?? item?.taxedprice ?? item?.price);

      salesItems.push({
        product_id: productId || itemKey,
        item_name: itemName,
        category_name: categoryName,
        total_quantity: quantity,
        total_amount: lineAmount,
        total_orders: seenInOrder.has(itemKey) ? 0 : 1,
      });
      seenInOrder.add(itemKey);
    });
  }));

  const categoryList = buildCategorySalesGroups(salesItems);

  return {
    mode,
    date: selectedDate,
    month: selectedMonth,
    restaurant: "",
    success: true,
    offline: true,
    total_items: categoryList.length,
    total_quantity: categoryList.reduce((sum, c) => sum + c.total_quantity, 0),
    total_amount: categoryList.reduce((sum, c) => sum + c.total_amount, 0),
    total_orders: ordersWithItems,
    cached_order_count: orders.length,
    missing_detail_count: missingDetailCount,
    items: categoryList,
  };
};

const offlineSourceMessage = (report) => {
  const missing = numberValue(report?.missing_detail_count);
  if (missing > 0) {
    return report?.full_offline
      ? `Full Offline — ${missing} saved sale${missing === 1 ? " has" : "s have"} no item details and could not be included.`
      : `Offline mode — ${missing} saved sale${missing === 1 ? " has" : "s have"} only header data. Re-run Offline Sales Sync for this period to download item details.`;
  }
  if (report?.full_offline) {
    return "Full Offline — category totals are calculated only from the isolated local database.";
  }
  return "Offline mode — category totals are calculated from the range-sync SQLite bills and pending orders.";
};

const DayWiseCategoryReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [mode, setMode] = useReportDateState(REPORT_MODE_DAY, "mode");
  const [date, setDate] = useReportDateState(today());
  const [month, setMonth] = useReportDateState(todayMonth(), "month");
  const [report, setReport] = useState(
    buildDefaultReport({ mode: REPORT_MODE_DAY, date: today(), month: todayMonth() })
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setSourceMessage] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const reportRef = useRef(null);
  const storedUser = useMemo(
    () => JSON.parse(localStorage.getItem("userData") || "{}")?.data || {},
    []
  );

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, mode, date, month]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;

    const activeDate = date;
    const activeMonth = month;
    const isMonthMode = mode === REPORT_MODE_MONTH;
    const endpoint = isMonthMode ? MONTH_ENDPOINT : DAY_ENDPOINT;
    const body = isMonthMode
      ? { restaurantid: userId, month: activeMonth }
      : { restaurantid: userId, date: activeDate };

    opts.refresh ? setRefreshing(true) : setLoading(true);
    setSourceMessage("");

    try {
      if (isFullOfflineModeEnabled()) {
        const offlineReport = await getOfflineCategorySalesReport(userId, {
          mode,
          date: activeDate,
          month: activeMonth,
        });
        setReport({
          ...buildDefaultReport({ mode, date: activeDate, month: activeMonth }),
          ...offlineReport,
          items: Array.isArray(offlineReport?.items) ? offlineReport.items : [],
        });
        setSourceMessage(offlineSourceMessage(offlineReport));
        setPage(0);
        return;
      }
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        const offlineReport = await buildOfflineCategoryReport(userId, mode, activeDate, activeMonth);
        if (!offlineReport) throw new Error(json?.message || "Failed to load report");
        setReport(offlineReport);
        setSourceMessage(offlineSourceMessage(offlineReport));
        setPage(0);
        return;
      }

      const items = await aggregateCategories(Array.isArray(json?.items) ? json.items : [], userId);
      const liveOrCachedReport = {
        mode,
        date: json?.date || activeDate,
        month: json?.month || activeMonth,
        restaurant: json?.restaurant || "",
        success: true,
        offline: false,
        total_items: items.length,
        total_quantity: Number(
          json?.total_quantity ??
            items.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0)
        ),
        total_amount: Number(
          json?.total_amount ??
            items.reduce((sum, item) => sum + Number(item.total_amount || 0), 0)
        ),
        total_orders: Number(
          json?.total_orders ??
            items.reduce((sum, item) => sum + Number(item.total_orders || 0), 0)
        ),
        items,
      };

      if (res.headers.get(DESKTOP_CACHE_HEADER) === "1") {
        const offlineReport = await buildOfflineCategoryReport(userId, mode, activeDate, activeMonth);
        if (offlineReport?.items?.length || !liveOrCachedReport.items.length) {
          setReport(offlineReport || liveOrCachedReport);
          setSourceMessage(
            offlineReport
              ? offlineSourceMessage(offlineReport)
              : "Offline mode — showing the last saved category report for this selection."
          );
        } else {
          setReport(liveOrCachedReport);
          setSourceMessage("Offline mode — showing the last saved category report for this selection.");
        }
      } else {
        setReport(liveOrCachedReport);
      }
      setPage(0);
    } catch (error) {
      try {
        const offlineReport = await buildOfflineCategoryReport(userId, mode, activeDate, activeMonth);
        if (offlineReport) {
          setReport(offlineReport);
          setSourceMessage(offlineSourceMessage(offlineReport));
          setPage(0);
        } else {
          toast.error(error.message || "Unable to load report");
          setReport(buildDefaultReport({ mode, date: activeDate, month: activeMonth }));
        }
      } catch (offlineError) {
        toast.error(error.message || "Unable to load report");
        setReport(buildDefaultReport({ mode, date: activeDate, month: activeMonth }));
      }
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtINR = (val) =>
    formatStoredMoney(val, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return { start, end, rows: report.items.slice(start, end) };
  }, [page, report.items]);

  const pageCount = Math.ceil((report.items.length || 0) / rowsPerPage);

  const exportCsv = () => {
    const rows = report.items.map((cat, idx) => [
      idx + 1,
      cat.category_name,
      cat.item_count,
      cat.total_quantity,
      cat.total_orders,
      cat.total_amount,
    ]);
    downloadCSV(
      `category_report_${mode}_${mode === REPORT_MODE_MONTH ? month : date}.csv`,
      ["#", "Category", "Items", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportExcel = () => {
    const rows = report.items.map((cat, idx) => [
      idx + 1,
      cat.category_name,
      cat.item_count,
      cat.total_quantity,
      cat.total_orders,
      cat.total_amount,
    ]);
    downloadExcel(
      `category_report_${mode}_${mode === REPORT_MODE_MONTH ? month : date}.xlsx`,
      ["#", "Category", "Items", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportPdf = () =>
    downloadPDF(
      reportRef.current,
      `category_report_${mode}_${mode === REPORT_MODE_MONTH ? month : date}.pdf`
    );

  const buildThermalReceipt = (lineWidth) =>
    buildCategorySalesReceiptText(
      {
        restaurant:
          report.restaurant ||
          storedUser.restaurant_name ||
          storedUser.name ||
          "Bill Sarthi",
        addressLines: [
          storedUser.addressline1 || storedUser.address || "",
          storedUser.addressline2 || "",
          storedUser.addressline3 || "",
        ],
        registrationLines: [
          storedUser.customfield1 || "",
          storedUser.customfield2 || "",
          storedUser.customfield3 || "",
          storedUser.gstno ? `GSTIN: ${storedUser.gstno}` : "",
        ],
        periodLabel: getPeriodLabel(mode, date, month),
        printedAt: new Date(),
        categories: report.items,
      },
      lineWidth
    );

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader
        title="Day-wise Category Report"
        subtitle="Category-wise totals by day or month with aggregated sales."
        stats={[
          { label: "Categories", value: report.total_items || 0 },
          { label: "Total Qty", value: report.total_quantity || 0 },
          { label: "Total Orders", value: report.total_orders || 0 },
          { label: "Total Amount", value: fmtINR(report.total_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker
          mode={mode}
          onModeChange={(m) => setMode(m)}
          date={mode === REPORT_MODE_DAY ? date : undefined}
          onDateChange={(d) => setDate(d)}
          month={mode === REPORT_MODE_MONTH ? month : undefined}
          onMonthChange={(m) => setMonth(m)}
        />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>


      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading category data...</Center>
        ) : report.items.length === 0 ? (
          <Center>No category sales found for this selection.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th>Qty</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginated.rows.map((category, idx) => (
                  <React.Fragment key={category.category_name || idx}>
                    <CategoryHeadingRow>
                      <td colSpan="3">{category.category_name}</td>
                    </CategoryHeadingRow>
                    {(category.items || []).map((item, itemIndex) => (
                      <tr key={`${item.product_id || item.item_name}-${itemIndex}`}>
                        <td>{item.item_name}</td>
                        <NumberCell>{item.total_quantity}</NumberCell>
                        <NumberCell>{fmtINR(item.total_amount)}</NumberCell>
                      </tr>
                    ))}
                    <CategoryTotalRow>
                      <td>Category Total</td>
                      <NumberCell>{category.total_quantity}</NumberCell>
                      <NumberCell>{fmtINR(category.total_amount)}</NumberCell>
                    </CategoryTotalRow>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <GrandTotalRow>
                  <td>Grand Total</td>
                  <NumberCell>{report.total_quantity}</NumberCell>
                  <NumberCell>{fmtINR(report.total_amount)}</NumberCell>
                </GrandTotalRow>
              </tfoot>
            </Table>
          </TableWrap>
        )}

        <Pager>
          <PageBtn disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </PageBtn>
          <PageInfo>
            Page <strong>{pageCount ? page + 1 : 0}</strong> of <strong>{pageCount}</strong>
          </PageInfo>
          <PageBtn disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </PageBtn>
        </Pager>

        <ActionsRow style={{ marginTop: 14 }}>
          <PrimaryBtn onClick={exportExcel}>Excel</PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn
            onClick={() =>
              printThermal(
                reportRef.current,
                "Item Sales Register",
                { buildReceiptText: buildThermalReceipt }
              )
            }
          >
            Thermal Print
          </PrimaryBtn><PrimaryBtn
            onClick={() =>
              printThermalViaUsb(
                reportRef.current,
                "Item Sales Register",
                { buildReceiptText: buildThermalReceipt }
              )
            }
          >
            Thermal Print via USB
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </ActionsRow>
      </Card>
    </Page>
  );
};

export default DayWiseCategoryReportPage;

const today = () => toISO(new Date());
const todayMonth = () => formatMonth(new Date());
const toISO = (d) => toLocalYmd(d);
const formatMonth = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};
const displayDate = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${day}-${month}-${year}` : String(value || "");
};
const getPeriodLabel = (mode, date, month) => {
  if (mode !== REPORT_MODE_MONTH) {
    const selected = displayDate(date);
    return `From ${selected} to ${selected}`;
  }
  const [year, monthNumber] = String(month || "").split("-").map(Number);
  if (!year || !monthNumber) return `Month: ${month}`;
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return `From 01-${String(monthNumber).padStart(2, "0")}-${year} to ${String(lastDay).padStart(2, "0")}-${String(monthNumber).padStart(2, "0")}-${year}`;
};

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary-hover));
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(7, 95, 158, 0.15);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(7, 95, 158, 0.25);
  }
`;


const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 24px 0;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    color: #334155;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
  }
  tr:hover td {
    background: #f8fafc;
  }
`;

const CategoryHeadingRow = styled.tr`
  td {
    text-align: center;
    padding: 13px 14px;
    background: #eef6ff;
    color: #0f4f7d;
    font-weight: 900;
    font-size: 15px;
    border-top: 2px solid #bddcf4;
  }
  &:hover td {
    background: #eef6ff;
  }
`;

const CategoryTotalRow = styled.tr`
  td {
    background: #f8fafc;
    font-weight: 900;
    border-top: 1px dashed #94a3b8;
    border-bottom: 2px solid #cbd5e1;
  }
  &:hover td {
    background: #f8fafc;
  }
`;

const GrandTotalRow = styled.tr`
  td {
    background: #e8f3fc;
    color: #0f3f61;
    font-weight: 900;
    border-top: 2px solid var(--app-primary);
  }
`;

const NumberCell = styled.td`
  text-align: right !important;
  font-variant-numeric: tabular-nums;
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 7px 14px;
  font-weight: 700;
  color: var(--app-primary);
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  color: #64748b;
  font-size: 0.88rem;
`;
